import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useMatch } from "react-router-dom";
import { Form, InputGroup, Modal, Button } from "react-bootstrap";
import { BiSearch } from "react-icons/bi";
import Loading from "../Components/Loading";
import { useLanguage } from "../Components/Localization/LanguageContext";
import { useDebounce } from "@uidotdev/usehooks";
import * as api from "../api/api";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

// Dummy strings for localization (replace with your actual strings object)
const strings = {
  HouseDetailsViewer: {
    en: {
      pageTitle: "House Details",
      searchPlaceholder: "Search by Key",
      houseId: "House ID",
      key: "Key",
      value: "Value",
      noResults: "No results found for your search.",
      loading: "Loading house details...",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      // New strings for modals
      confirmCancelTitle: "Discard Changes?",
      confirmCancelBody:
        "You have unsaved changes. Are you sure you want to discard them and go back?",
      confirmDeleteTitle: "Confirm Deletion",
      confirmDeleteBody:
        "Are you sure you want to permanently delete this house? This action cannot be undone.",
      yes: "Yes",
      no: "No",
      ok: "Ok",
    },
    de: {
      pageTitle: "Hausdetails",
      searchPlaceholder: "Nach Schlüssel suchen",
      houseId: "Haus-ID",
      key: "Schlüssel",
      value: "Wert",
      noResults: "Keine Ergebnisse für Ihre Suche gefunden.",
      loading: "Hausdetails werden geladen...",
      delete: "Löschen",
      save: "Speichern",
      cancel: "Abbrechen",
      // New strings for modals
      confirmCancelTitle: "Änderungen verwerfen?",
      confirmCancelBody:
        "Sie haben ungespeicherte Änderungen. Möchten Sie diese wirklich verwerfen und zurückgehen?",
      confirmDeleteTitle: "Löschen bestätigen",
      confirmDeleteBody:
        "Möchten Sie dieses Haus wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
      yes: "Ja",
      no: "Nein",
      ok: "Ok",
    },
  },
};

const emailFields = [
  "(Person 1) E-Mail",
  "(Person 2) E-Mail",
  "(Person 3) E-Mail",
  "(Person 4) E-Mail",
  "(Person 5) E-Mail",
  "E-Mail bei Bedrohung",
  "E-Mail Warenannahme",
  "E-Mail-Adresse der Filiale",
];
const emailValidationRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Helper function to calculate estimated rows for a textarea,
 * considering newlines and tab characters.
 * @param {string} value The string content of the textarea.
 * @param {number} charsPerLine The estimated number of characters that fit on one line.
 * @param {number} tabWidth The number of spaces a tab character should represent.
 * @returns {number} The estimated number of rows the textarea needs.
 */
const calculateEstimatedRows = (value, charsPerLine = 120, tabWidth = 4) => {
  if (!value) return 1;

  // Replace tabs with equivalent spaces for accurate width calculation
  const processedValue = value.replace(/\t/g, " ".repeat(tabWidth));

  const lines = processedValue.split("\n");
  let totalRows = 0;

  lines.forEach((line) => {
    // For each line, calculate how many rows it takes based on charsPerLine
    // Math.max(1, ...) ensures even a single character line takes at least 1 row
    totalRows += Math.max(1, Math.ceil(line.length / charsPerLine));
  });

  return Math.max(1, totalRows); // Ensure a minimum of 1 row overall
};

function isValidURL(str) {
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}


function AdminHousePage() {
  let { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const pageTitle = "HouseDetailsViewer";

  // Matches like: /agent/house/did/did=12345&tenant=37
  const checkA = useMatch("/agent/house/did/:params");
  const checkB = useMatch("/agent/house/byId/:id");

  const matchAgentRoute = checkA || checkB;
  const isViewing = !!matchAgentRoute;

  const [houseData, setHouseData] = useState(null);
  const [houseDetails, setHouseDetails] = useState([]);
  const [initialHouseDetails, setInitialHouseDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search states
  const [inputValue, setInputValue] = useState("");
  const debouncedSearchValue = useDebounce(inputValue, 500);

  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Selected Houses
  const [allHouses, setAllHouses] = useState([]);
  const [selectedHouseId, setSelectedHouseId] = useState(id);

  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const reorderDetails = (details) => {
    if (details) {
      const idx = details.findIndex(
        (detail) => detail.detailKey === "Standardöffnungszeiten Filiale"
      );
      if (idx !== -1) {
        const standardOpeningTimesDetail = details.splice(idx, 1)[0];
        details.unshift(standardOpeningTimesDetail);
      }
    }
    return details;
  };

  useEffect(() => {
    setIsLoading(true);

    api.getAllHousesUnpaged().then((response) => {
      setAllHouses(response.data.object.houses);
    });

    const fetchHouseDetails = async () => {
      try {
        let response;

        if (!isViewing) {
          // Admin normal house view
          const houseId = id;
          response = await api.getHouseById(houseId);
        } else {
          const isDidRoute =
            window.location.pathname.includes("/agent/house/did/");
          if (isDidRoute && checkA?.params?.params) {
            // Extract did and tenantId from the params string
            // e.g., "did=12345&tenant=37"
            const queryString = checkA.params.params;
            const queryParams = new URLSearchParams(queryString);

            let houseDid = queryParams.get("did");
            let tenantId = queryParams.get("tenant");

            // put back the tenantId in the sessionStorage
            sessionStorage.setItem(
              "userTenant",
              JSON.stringify({ tenantId: tenantId })
            );

            if (houseDid) {
              houseDid = houseDid.trim();
              houseDid = "%2B" + houseDid;
              console.log(houseDid, tenantId);

              response = await api.getHouseByDidAgent(houseDid, tenantId);
              api.getAllHousesUnpagedTenant(tenantId).then((response) => {
                setAllHouses(response.data.object.houses);
              });
            }
          } else {
            // Fallback to byId agent route
            let houseId = id;
            response = await api.getHouseByIdAgent(houseId);
          }
        }

        const fetchedHouseObject = response.data.object;

        fetchedHouseObject.details = reorderDetails(fetchedHouseObject.details);

        setHouseData(fetchedHouseObject);
        setHouseDetails([...fetchedHouseObject.details]);
        setInitialHouseDetails(
          JSON.parse(JSON.stringify(fetchedHouseObject.details))
        );

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching house details:", error);
        setIsLoading(false);
        if (!isViewing) {
          navigate("/admin/houses");
        }
      }
    };

    fetchHouseDetails();
  }, [id, navigate, isViewing, checkA]);

  const navigateNewHouse = (newHouseId) => {
    const url = isViewing
      ? `/agent/house/byId/${newHouseId}`
      : `/house/${newHouseId}`;
    navigate(url, { replace: true });
  };

  // --- START: FILTERING LOGIC ---
  const currentFilteredDetails = useMemo(() => {
    if (!houseDetails || houseDetails.length === 0) {
      return [];
    }
    const searchTerm = debouncedSearchValue.toLowerCase();
    return houseDetails.filter((detail) =>
      detail.detailKey.toLowerCase().includes(searchTerm)
    );
  }, [houseDetails, debouncedSearchValue]); // Dependencies for memoization
  // --- END: FILTERING LOGIC ---

  // Handle changes in the textarea values
  const handleDetailValueChange = (idToUpdate, newValue) => {
    // Only allow changes if not in viewing mode
    if (!isViewing) {
      setHouseDetails((prevDetails) =>
        prevDetails.map((detail) =>
          detail.id === idToUpdate
            ? { ...detail, detailValue: newValue }
            : detail
        )
      );
    }
  };

  // Check for unsaved changes (relevant only if not in viewing mode)
  const hasUnsavedChanges = () => {
    if (isViewing) return false;
    // Compare the current houseDetails array with the initial deep copy
    return JSON.stringify(houseDetails) !== JSON.stringify(initialHouseDetails);
  };

  // Handle Cancel button click
  const handleCancelClick = () => {
    if (isViewing) {
      window.close();
    } else {
      if (hasUnsavedChanges()) {
        setShowCancelModal(true);
      } else {
        navigate(-1);
      }
    }
  };

  // Confirm discard changes and navigate back
  const confirmDiscardChanges = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  // Handle Delete button click
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // Confirm permanent deletion
  const confirmPermanentDelete = async () => {
    setShowDeleteModal(false);
    setIsLoading(true); // Show loading during deletion

    try {
      await api.deleteHouseById(houseData.houseId);
      setIsLoading(false); // Hide loading after deletion
      navigate("/housemanagement"); // Navigate to the houses page
    } catch (error) {
      console.error("Error deleting house:", error);
      setIsLoading(false); // Hide loading after error
      // Optionally, show an error message to the user
    }
  };

  // Handle Save button click
  const handleSaveClick = async () => {
    if (!houseData || !houseDetails) {
      console.error("House data not loaded yet for saving.");
      return;
    }

    // Prepare payload with only changed details
    const changedDetails = houseDetails
      .filter((currentDetail) => {
        const initialDetail = initialHouseDetails.find(
          (d) => d.id === currentDetail.id
        );
        return (
          initialDetail &&
          currentDetail.detailValue !== initialDetail.detailValue
        );
      })
      .map((detail) => ({
        id: detail.id,
        detailKey: detail.detailKey,
        detailValue: detail.detailValue,
      }));

    if (changedDetails.length > 0) {
      const updatePayload = changedDetails;

      // Validate if the changedDetails contains one of the emailFields,

      if (
        updatePayload.some((detail) => emailFields.includes(detail.detailKey))
      ) {
        if (
          !updatePayload.every((detail) =>
            emailValidationRegex.test(detail.detailValue)
          )
        ) {
          console.error(
            "Invalid email format found in changedDetails.",
            updatePayload.every((detail) =>
              emailFields.includes(detail.detailKey)
            )
          );
          // Optionally, show an error message to the user
          const fieldsWithInvalidEmail = updatePayload
            .filter((detail) => !emailValidationRegex.test(detail.detailValue))
            .map((detail) => detail.detailKey);
          console.log("Fields with invalid email:", fieldsWithInvalidEmail);
          setSnackbarData({
            message:
              "Invalid email format found, check changed fields: " +
              fieldsWithInvalidEmail.join(", "),

            open: true,
            severity: "error",
          });
          return;
        }
      }

      console.log("Sending update:", updatePayload);
      setIsLoading(true); // Show loading during save

      try {
        const response = await api.updateHouseDetailsById(
          houseData.houseId,
          updatePayload
        );
        if (response.data.statusCode == 200) {
          console.log("Update successful!");
          setSnackbarData({
            message: "House details updated successfully.",
            open: true,
            severity: "success",
          });
          const updatedFetchedHouseObject = response.data.object;
          setHouseData(updatedFetchedHouseObject);
          setHouseDetails(reorderDetails(updatedFetchedHouseObject.details));
          setInitialHouseDetails(
            JSON.parse(JSON.stringify(updatedFetchedHouseObject.details))
          );
        }
      } catch (error) {
        console.error("Error updating house details:", error);
        // Handle error, e.g., show an error snackbar
        setSnackbarData({
          message: "Error updating house details.",
          open: true,
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log("No changes to save.");
      // Optionally, show a snackbar saying "No changes to save"
      setSnackbarData({
        message: "No changes to save.",
        open: true,
        severity: "info",
      });
    }
  };

  const handleSearchInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const [open, setOpen] = useState(false); // State for Autocomplete open/close

  if (isLoading) {
    return <Loading />;
  }

  // If houseData is null after loading, it means the house was not found or an error occurred
  if (!houseData) {
    return (
      <div className="main-content-container">
        <div className="main-content">
          <p>House not found or an error occurred.</p>
        </div>
      </div>
    );
  }

  // Find the Filialname from the details array for the header
  const filialNameDetail = houseData.details.find(
    (detail) => detail.detailKey === "Filialname"
  );
  const filialName = filialNameDetail ? filialNameDetail.detailValue : "N/A";

  return (
    <div
      className={`main-content-container ${isViewing ? "full-page-view" : ""}`}
      style={
        isViewing
          ? {
              padding: "0",
              margin: "0",
              maxWidth: "100%",
              width: "100vw",
              height: "100vh",
              overflow: "auto",
            }
          : {}
      }
    >
      <div
        className="main-content"
        style={
          isViewing
            ? {
                padding: "20px",
                maxWidth: "unset",
                width: "auto",
                minHeight: "100vh",
              }
            : {}
        }
      >
        <p className="main-content__title" style={{ fontSize: "20px" }}>
          {strings[pageTitle][language].pageTitle}: {houseData.houseId} -{" "}
          {filialName}
        </p>
        <Snackbar
          open={snackbarData.open}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          autoHideDuration={4000}
          onClose={() => {
            setSnackbarData({ ...snackbarData, open: false });
          }}
        >
          <Alert
            onClose={() => {
              setSnackbarData({ ...snackbarData, open: false });
            }}
            severity={snackbarData.severity}
          >
            {snackbarData.message}
          </Alert>
        </Snackbar>
        <Autocomplete
          id="searchable-house-autocomplete"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option, value) =>
            option.houseId === value.houseId
          }
          getOptionLabel={(option) => `${option.houseId} - ${option.name}`}
          options={allHouses}
          value={{ houseId: houseData.houseId, name: filialName }}
          onChange={(event, newValue) => {
            if (!newValue) return;
            setSelectedHouseId(newValue.houseId); // Still sets selected ID for consistency, though navigate handles route
            navigateNewHouse(newValue.houseId);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              style={{ paddingLeft: "10px", paddingRight: "10px" }}
              label="Search and Select a House"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          noOptionsText="No houses found"
        />
        <hr className="main-content-title__hr" />

        <div
          className="form-group row"
          style={{ width: "100%", marginLeft: "0px" }}
        >
          <InputGroup className="">
            <Form.Control
              placeholder={strings[pageTitle][language].searchPlaceholder}
              aria-label="Search"
              onChange={handleSearchInputChange}
              value={inputValue}
            />
            <InputGroup.Text id="basic-addon2">
              <BiSearch />
            </InputGroup.Text>
          </InputGroup>
        </div>

        <div className="main-content__body">
          <table
            style={{
              width: "98%",
              margin: "20px auto",
              borderCollapse: "collapse",
              border: "none",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "12px 8px",
                    width: "30%",
                    textAlign: "left",
                  }}
                >
                  {strings[pageTitle][language].key}
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    width: "70%",
                    textAlign: "left",
                  }}
                >
                  {strings[pageTitle][language].value}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentFilteredDetails.length > 0 ? (
                currentFilteredDetails.map((detail) => (
                  <tr key={detail.id}>
                    <td
                      style={{
                        padding: "8px",
                        verticalAlign: "top",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <strong>{detail.detailKey}</strong>
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        verticalAlign: "top",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {isViewing &&
                        detail.detailValue &&
                        (detail.detailValue.startsWith("http://") ||
                          detail.detailValue.startsWith("https://")) ? (
                          // 1. RENDER LINK IF VIEWING AND VALUE IS A URL
                        <a
                            href={detail.detailValue}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "block",
                              padding: "8px",
                              wordBreak: "break-all",
                              color: "#0d6efd",          // Bootstrap primary blue
                              fontSize: "1.15rem",       // slightly bigger text
                              textDecoration: "underline",
                              fontWeight: 500,
                            }}
                        >
                            {detail.detailValue}
                        </a>
                        ) : (
                          // 2. OTHERWISE RENDER THE TEXTAREA
                        <Form.Control
                            as="textarea"
                            rows={calculateEstimatedRows(detail.detailValue, 120, 4)}
                            value={detail.detailValue || ""}
                            onChange={(e) =>
                              handleDetailValueChange(detail.id, e.target.value)
                            }
                            readOnly={isViewing} // Still keeps it read-only if viewing but not a link
                            style={{ width: "100%", resize: "vertical" }}
                          />
                        )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="2"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {strings[pageTitle][language].noResults}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div
            style={{ borderBottom: "1px solid black", marginLeft: "1em" }}
          ></div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            {!isViewing && (
              <>
                <button
                  className="button-round"
                  style={{
                    alignSelf: "flex-end",
                    margin: "1em",
                    color: "red",
                    borderColor: "red",
                  }}
                  onClick={handleDeleteClick}
                >
                  {strings[pageTitle][language].delete}
                </button>
                <button
                  className="button-round"
                  style={{ alignSelf: "flex-end", margin: "1em" }}
                  onClick={handleSaveClick}
                  disabled={!hasUnsavedChanges()} // Disable save if no changes
                >
                  {strings[pageTitle][language].save}
                </button>
              </>
            )}
            <button
              className="button-round"
              style={{
                alignSelf: "flex-end",
                margin: "1em",
                borderColor: isViewing ? "#007bff" : "grey",
                color: isViewing ? "#007bff" : "grey",
              }}
              onClick={handleCancelClick}
            >
              {isViewing ? "Close Tab" : strings[pageTitle][language].cancel}
            </button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {strings[pageTitle][language].confirmCancelTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {strings[pageTitle][language].confirmCancelBody}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            {strings[pageTitle][language].no}
          </Button>
          <Button variant="primary" onClick={confirmDiscardChanges}>
            {strings[pageTitle][language].yes}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {strings[pageTitle][language].confirmDeleteTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {strings[pageTitle][language].confirmDeleteBody}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {strings[pageTitle][language].cancel}
          </Button>
          <Button variant="danger" onClick={confirmPermanentDelete}>
            {strings[pageTitle][language].ok}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminHousePage;
